import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { BarChart3, TrendingUp, Clock, Loader2 } from "lucide-react";
import ABTestResults from "./ABTestResults";

const TYPE_LABELS = {
  birthday_30d: "30-Day Countdown", birthday_21d: "21-Day", birthday_14d: "14-Day",
  birthday_7d: "7-Day", birthday_1d: "Day Before", expiring_soon: "Expiring Deals",
  new_deals_weekly: "Weekly Digest", deal_updated: "Deal Updated",
  badge_earned: "Badge Earned", level_up: "Level Up",
  monthly_challenge: "Monthly Challenge", streak_at_risk: "Streak at Risk",
  whats_new: "What's New", beta_updates: "Beta Updates", manual: "Manual Broadcast",
};

export default function NotificationAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/admin/notifications/analytics`);
        setData(d);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-xs text-slate-400 text-center py-4">Failed to load analytics</p>;

  const { overview, type_breakdown, daily_trends, top_reengagement_type } = data;
  const maxSent = Math.max(...(daily_trends || []).map(d => d.sent), 1);

  return (
    <div className="space-y-4" data-testid="notification-analytics">
      {/* Overview Metrics */}
      <OverviewMetrics overview={overview} />

      {/* 7-Day Trend Chart */}
      <TrendChart trends={daily_trends} maxSent={maxSent} topType={top_reengagement_type} />

      {/* Type Breakdown Table */}
      {type_breakdown?.length > 0 && <TypeBreakdown breakdown={type_breakdown} />}

      {/* A/B Test Results */}
      <ABTestResults />
    </div>
  );
}

function OverviewMetrics({ overview }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="analytics-overview">
      <MetricCard label="Sent (all time)" value={overview.total_sent} color="bg-indigo-50 border-indigo-200 text-indigo-700" />
      <MetricCard label="Sent (this month)" value={overview.month_sent} color="bg-violet-50 border-violet-200 text-violet-700" />
      <MetricCard label="Open Rate" value={`${overview.open_rate}%`} color="bg-emerald-50 border-emerald-200 text-emerald-700" />
      <MetricCard label="Click-Through" value={`${overview.click_rate}%`} color="bg-amber-50 border-amber-200 text-amber-700" />
    </div>
  );
}

function MetricCard({ label, value, color }) {
  const [bg, border, text] = color.split(" ");
  return (
    <div className={`${bg} border ${border} rounded-xl p-3 text-center`}>
      <p className={`text-xl font-black ${text}`}>{value}</p>
      <p className="text-[10px] font-semibold text-slate-500">{label}</p>
    </div>
  );
}

function TrendChart({ trends, maxSent, topType }) {
  if (!trends?.length) return null;
  return (
    <div className="bg-slate-50 rounded-xl border border-slate-200 p-4" data-testid="analytics-trends">
      <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5 mb-3">
        <TrendingUp className="w-3.5 h-3.5" /> 7-Day Trend
      </p>
      <div className="flex items-end gap-1.5 h-24">
        {trends.map((d, i) => {
          const height = maxSent > 0 ? Math.max((d.sent / maxSent) * 100, 4) : 4;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`trend-bar-${i}`}>
              <span className="text-[9px] font-bold text-slate-500">{d.sent}</span>
              <div className="w-full relative rounded-t-md overflow-hidden" style={{ height: `${height}%` }}>
                <div className="absolute inset-0 bg-indigo-400 rounded-t-md" />
                {d.open_rate > 0 && (
                  <div className="absolute bottom-0 left-0 right-0 bg-emerald-400 rounded-t-sm" style={{ height: `${d.open_rate}%` }} />
                )}
              </div>
              <span className="text-[9px] text-slate-400">{d.label}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-[9px] text-slate-400"><span className="w-2 h-2 rounded-full bg-indigo-400" />Sent</span>
          <span className="flex items-center gap-1 text-[9px] text-slate-400"><span className="w-2 h-2 rounded-full bg-emerald-400" />Opens</span>
        </div>
        {topType && (
          <span className="text-[9px] text-indigo-500 font-medium flex items-center gap-1">
            <BarChart3 className="w-3 h-3" /> Top: {TYPE_LABELS[topType] || topType}
          </span>
        )}
      </div>
    </div>
  );
}

function TypeBreakdown({ breakdown }) {
  return (
    <div data-testid="analytics-type-breakdown">
      <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5 mb-2">
        <BarChart3 className="w-3.5 h-3.5" /> By Notification Type
      </p>
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="grid grid-cols-[1fr_50px_55px_55px_50px] gap-0 px-3 py-1.5 bg-slate-50 border-b border-slate-100">
          <span className="text-[9px] font-bold text-slate-400 uppercase">Type</span>
          <span className="text-[9px] font-bold text-slate-400 uppercase text-right">Sent</span>
          <span className="text-[9px] font-bold text-slate-400 uppercase text-right">Open %</span>
          <span className="text-[9px] font-bold text-slate-400 uppercase text-right">Click %</span>
          <span className="text-[9px] font-bold text-slate-400 uppercase text-right">Best Hr</span>
        </div>
        {breakdown.map((row, i) => (
          <div key={row.type} className={`grid grid-cols-[1fr_50px_55px_55px_50px] gap-0 px-3 py-2 ${i % 2 === 0 ? "" : "bg-slate-50/50"}`} data-testid={`type-row-${row.type}`}>
            <span className="text-[10px] font-medium text-slate-700 truncate">{TYPE_LABELS[row.type] || row.type}</span>
            <span className="text-[10px] text-slate-600 text-right font-mono">{row.sent}</span>
            <span className="text-[10px] text-emerald-600 text-right font-mono">{row.open_rate}%</span>
            <span className="text-[10px] text-amber-600 text-right font-mono">{row.click_rate}%</span>
            <span className="text-[10px] text-slate-400 text-right font-mono flex items-center justify-end gap-0.5">
              {row.best_hour ? <><Clock className="w-2.5 h-2.5" />{row.best_hour}</> : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
